import {useState, useEffect} from 'react';

import {FormRegularCheckIcon} from 'shells/icon';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {BaseRow} from 'shells/row/base-row/base-row';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {FormSubmissionTimestamp} from '../../../form-submissions/form-submission-timestamp';

import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {WidgetSeeAllFooter} from '../../../../core-entity/components/widget-see-all-footer';
import {OpenableModals} from '../../../../openable-modals/openable-modals';
import {useOpenableModal} from '../../../../openable-modals/use-openable-modal';

import {useGetContactPageFormSubmissions} from './graphql/hooks/use-get-contact-page-form-submissions';

import styles from './form-submissions-widget.module.css';

interface Props {
    contactId: string;
    widget: WidgetFragment;
}

export function FormSubmissionsWidget(props: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);

    const {submissions, totalCount, isLoading, isFetchingMore, fetchAllSubmissions, hasNextPage} =
        useGetContactPageFormSubmissions(props.contactId);

    const isEmpty = totalCount === 0;

    const selectedSubmission = selectedSubmissionId
        ? submissions?.edges.find((submission) => submission.node.id === selectedSubmissionId)?.node
        : null;
    const {onOpen: onOpenSubmissionModal} = useOpenableModal(OpenableModals.FORM_SUBMISSION_MODAL, {
        submissionId: selectedSubmissionId,
        submissionTime: selectedSubmission?.submissionTime,
        form: selectedSubmission?.formVersion.form,
    });

    useEffect(() => {
        if (selectedSubmissionId) {
            onOpenSubmissionModal();
            setSelectedSubmissionId(null);
        }
    }, [selectedSubmissionId, onOpenSubmissionModal]);

    const handleSeeAll = () => {
        setIsExpanded(true);
        fetchAllSubmissions();
    };

    const submissionRows = submissions?.edges.map((submission) => (
        <BaseRow
            title={submission.node.formVersion.form.name || 'Untitled form'}
            key={submission.node.id}
            type='form-submissions'
            onClick={() => {
                setSelectedSubmissionId(submission.node.id);
            }}
        >
            <FormSubmissionTimestamp
                submissionTime={submission.node.submissionTime}
                prependSubmitted={false}
            />
        </BaseRow>
    ));

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            // Forgoing shared helper for now due to coupling to deprecated
            // widget systems
            emptyStateProps={{
                isEmpty,
                text: 'No form submissions yet',
                icon: FormRegularCheckIcon,
                iconSize: 22,
            }}
            isLoading={isLoading}
            count={totalCount}
        >
            {submissionRows && (
                <>
                    <div className={styles['scroll-container']}>
                        {submissionRows}
                        {isExpanded && isFetchingMore && <LoadingPage padding={8} />}
                    </div>
                </>
            )}
            <WidgetSeeAllFooter
                seeAllProps={{
                    isExpanded: isExpanded,
                    hasMore: hasNextPage,
                    term: 'form submissions',
                    onClick: handleSeeAll,
                }}
            />
        </CollapsibleCardWidget>
    );
}
